import {
  CellClassParams,
  ColDef,
  FilterChangedEvent,
  GetContextMenuItemsParams,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  ISetFilterParams,
  MenuItemDef,
  SelectionChangedEvent,
} from '@ag-grid-community/core'
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  HeliosColumnTypes,
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosDefaultToolPanels,
  heliosMultiRowSelectionConfig,
} from '@helios/ag-grid-config'
import { FilterModalService } from '@helios/filter-modal'
import { countFilters } from '@helios/server-side-util'
import {
  RootState,
  selectApprovalRequestSubmitted,
  setFocusedApprovalsAccountId,
} from '@helios/state-management'
import {
  createFilterGroupsToolPanel,
  DensityLevel,
  FilterGroupsToolPanelParams,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
} from '@helios/user-preferences'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfColumnTypes } from '@northfork/ag-grid'
import { AgGridState } from '@northfork/ag-grid-angular'
import {
  composeFieldPath,
  formatDate,
  ISODateTimeFormat,
} from '@northfork/basics'
import { formatDistanceToNow } from 'date-fns'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'
import { of, Subject, Subscription } from 'rxjs'
import { distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { ApprovalsKey } from '../../../panel-key'
import {
  evaluateErrorCount,
  getCellStyleForRequestStatus,
} from '../../models/approvals-util'
import { Requests } from '../../models/approvals.model'
import { ApprovalsService } from '../../services/approvals.service'
import { ApprovalsHelpComponent } from '../approvals-help/approvals-help.component'

const RequestGridKey = `${ApprovalsKey}-RequestsGrid`

@Component({
  selector: 'hs-requests-grid',
  templateUrl: './requests-grid.component.html',
  styleUrls: ['./requests-grid.component.scss'],
  providers: [FilterModalService],
})
export class RequestsGridComponent implements OnInit, OnDestroy {
  data: Requests[] = []
  field = composeFieldPath(this.data)
  gridApi?: AgGridApi
  isLoading = false
  lastRefreshed: Date | null = null
  private modalSubscription: Subscription | undefined
  clearFiltersOnAccountLoadToggle = new FormControl(true)

  @Output()
  $requestRowSelected = new EventEmitter<Requests[]>()

  @Input() set isRequestLoading(isLoading: boolean) {
    this.isLoading = isLoading
  }

  @Input() set refreshRequest(refresh: boolean) {
    if (refresh) {
      this.loadRequests()
    }
  }
  clearFiltersEnabled = false
  filterCount = 0
  private readonly destroySubject$ = new Subject<void>()
  savedFilterState: { [columnName: string]: unknown } | undefined

  constructor(
    private readonly preferences: UserPreferencesService,
    private readonly requestsGridService: ApprovalsService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly modalService: NgbModal,
    private readonly store: Store<RootState>,
    public filterModalService: FilterModalService,
  ) {}

  ngOnInit(): void {
    this.loadRequests()
    this.store
      .select(selectApprovalRequestSubmitted)
      .pipe(distinctUntilChanged())
      .subscribe(approvalRequestSubmitted => {
        if (approvalRequestSubmitted) this.loadRequests()
      })

    this.clearFiltersOnAccountLoadToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })
  }
  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
    if (this.modalSubscription) {
      this.modalSubscription.unsubscribe()
    }
  }

  refreshRequestPage() {
    this.$requestRowSelected.emit(undefined)
    this.loadRequests()
  }
  openHelpModal() {
    const helpModal = this.modalService.open(ApprovalsHelpComponent, {
      size: 'lg',
      windowClass: 'modal-window-click-through',
      backdrop: false,
      keyboard: false,
    } as NgbModalOptions)
    this.modalSubscription = helpModal.closed.subscribe(() => {})
  }

  loadRequests() {
    this.isLoading = true

    this.requestsGridService
      .getRequestsList()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(requests => {
        this.data = requests.requests
        this.isLoading = false
        this.updateLastRefreshed()
      })
  }
  gridOptions: GridOptions = {
    ...heliosMultiRowSelectionConfig,
    getContextMenuItems: params => this.getContextMenuItems(params),
    defaultColDef: {
      ...heliosDefaultColDef,
    },
    sideBar: generateHeliosAgGridSidebarFromScratch([
      createPanelSettingsToolPanel({
        controls: [
          {
            label: 'Clear Filters on Load',
            formControl: this.clearFiltersOnAccountLoadToggle,
          },
        ],
      }),
      ...heliosDefaultToolPanels,
      createFilterGroupsToolPanel<unknown>(this.filterGroupsToolPanelParams),
    ]),
    columnDefs: this.createColumnDefs(),
    onSelectionChanged: event => {
      this.onRowSelectionChanged(event)
    },
    onFirstDataRendered: () => this.applyFiltersAfterFirstRender(),
    onFilterChanged: params => this.onFilterChanged(params),
    onGridReady: params => this.onGridReady(params),
    icons: {
      panelSettings: generateMdiIcon('mdi-cog'),
    },
  }
  /** @internal */
  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(
          params.api,
          DensityLevel.Compact === level,
        ),
      )

    this.filterModalService.registerGrid(
      this.gridApi?.api,
      ApprovalsKey,
      RequestGridKey,
    )

    enableHeliosMultiRowDragSelection(this.gridApi.api)
    enableHeliosCopyBehavior(this.gridApi.api)

    const agGridStateSaved = await this.savedViewService.manageSavedView(
      params,
      RequestGridKey,
      this.destroySubject$,
      true,
      this.getCurrentPanelSettingsObject(),
    )
    this.savedFilterState = agGridStateSaved.filterState
    this.applyGridConfig(agGridStateSaved)
  }

  onRowSelectionChanged(event: SelectionChangedEvent) {
    const selectedRequests = event.api
      .getSelectedNodes()
      .map(x => x.data as Requests)
    this.$requestRowSelected.emit(selectedRequests)
  }

  applyFiltersAfterFirstRender() {
    // apply filter model when it has any values in it
    if (Object.keys(this.savedFilterState ?? {}).length) {
      if (this.savedFilterState) {
        this.gridApi?.api.setFilterModel(this.savedFilterState)
      }
    }
  }

  get filterGroupsToolPanelParams(): FilterGroupsToolPanelParams<unknown> {
    return {
      gridKey: RequestGridKey,
      getExternalFilters: () => ({}),
      setExternalFilters: () => undefined,
      cacheLoaded: of(true),
    }
  }

  createColumnDefs(): ColDef[] {
    return [
      {
        field: this.field('requestId'),
        headerName: 'Request ID',
        type: NfColumnTypes.Integer,
      },
      {
        field: this.field('requestedBy'),
        headerName: 'Username',
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          values: params => {
            this.requestsGridService
              .getUserNames()
              .subscribe(values => params.success(values))
          },
          closeOnApply: true,
        } as ISetFilterParams,
      },
      {
        field: this.field('reporter'),
        headerName: 'Reports To',
        filter: 'agSetColumnFilter',
      },
      {
        field: this.field('comment'),
        headerName: 'Justification',
      },
      {
        field: this.field('numUltimates'),
        headerName: 'Ult Parent Client ID Count',
        type: NfColumnTypes.Integer,
      },
      {
        field: this.field('numAccounts'),
        headerName: 'Account ID Count',
        type: NfColumnTypes.Integer,
      },
      {
        field: this.field('totalCommands'),
        headerName: 'Total Commands',
        type: NfColumnTypes.Integer,
      },
      {
        field: this.field('reconDate'),
        headerName: 'Request Recon Date',
        type: NfColumnTypes.Date,
      },
      {
        field: this.field('lastReconRunDate'),
        headerName: 'Latest Recon Date',
        type: NfColumnTypes.Date,
      },
      {
        field: this.field('totalErrors'),
        headerName: 'Active Errors',
        type: NfColumnTypes.Integer,
        cellRenderer: (params: ICellRendererParams) =>
          evaluateErrorCount(params.data.totalErrors),
      },
      {
        field: this.field('missingExternalPosErrCount'),
        headerName: 'Missing External Position Errors',
        type: NfColumnTypes.Integer,
        cellRenderer: (params: ICellRendererParams) =>
          evaluateErrorCount(params.data.missingExternalPosErrCount),
      },
      {
        field: this.field('highRiskErrCount'),
        headerName: 'High Risk Errors',
        type: NfColumnTypes.Integer,
        cellRenderer: (params: ICellRendererParams) =>
          evaluateErrorCount(params.data.highRiskErrCount),
      },
      {
        field: this.field('otherErrCount'),
        headerName: 'Other Errors',
        type: NfColumnTypes.Integer,
        cellRenderer: (params: ICellRendererParams) =>
          evaluateErrorCount(params.data.otherErrCount),
      },
      {
        field: this.field('createdAt'),
        headerName: 'Created TS',
        type: HeliosColumnTypes.DateTime,
        valueFormatter: params => {
          return (
            formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat) ??
            params.value
          )
        },
        width: heliosDefaultColWidth.DateTime,
      },
      {
        field: this.field('status'),
        headerName: 'Request Status',
        cellStyle: (params: CellClassParams) => {
          return getCellStyleForRequestStatus(params)
        },
        filter: 'agSetColumnFilter',
      },
      {
        field: this.field('reviewer'),
        headerName: 'Reviewed By',
        filter: 'agSetColumnFilter',
      },
      {
        field: this.field('actionTimestamp'),
        headerName: 'Review TS',
        type: HeliosColumnTypes.DateTime,
        valueFormatter: params => {
          return (
            formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat) ??
            params.value
          )
        },
        width: heliosDefaultColWidth.DateTime,
      },
      {
        field: this.field('reviewComment'),
        headerName: 'Reviewer Comment',
      },
      {
        field: this.field('actionType'),
        headerName: 'Action Type',
      },
      {
        field: this.field('tradingDataFeedCount'),
        headerName: 'Trading Data Feed Count',
        type: NfColumnTypes.Integer,
      },
      {
        field: this.field('custodyDataFeedCount'),
        headerName: 'Custody Data Feed Count',
        type: NfColumnTypes.Integer,
      },
    ]
  }

  private updateLastRefreshed(): void {
    this.lastRefreshed = new Date()
  }

  getTimeAgo(): string {
    return this.lastRefreshed
      ? formatDistanceToNow(this.lastRefreshed, { addSuffix: true })
      : ''
  }

  getContextMenuItems(params: GetContextMenuItemsParams) {
    const menuItems: ('separator' | MenuItemDef)[] = []

    let rowsSelected: Requests[]
    if (!params?.node?.id) {
      return menuItems
    }

    if (params.api.getSelectedNodes().length > 1) {
      rowsSelected = params.api.getSelectedNodes().map(t => t.data) ?? []
    } else {
      rowsSelected = [params.node.data] as Requests[]
    }

    menuItems.push({
      name: `Filter to Accounts`,
      action: () => this.filterAccounts(rowsSelected),
      disabled: !rowsSelected.every(x => x.accountIds != null),
    })

    return menuItems
  }

  filterAccounts(selectedRequest: Requests[]) {
    const accountIds = selectedRequest.flatMap(x => x.accountIds)
    this.store.dispatch(setFocusedApprovalsAccountId(accountIds))
  }
  clearFilters() {
    this.gridApi?.api.setFilterModel(null)
  }

  onFilterChanged(params: FilterChangedEvent) {
    const filtersPresent = this.gridApi?.api.isAnyFilterPresent() ?? false
    this.clearFiltersEnabled = filtersPresent
    this.countFilters()
  }

  private countFilters() {
    if (!this.gridApi) {
      return
    }
    const filterModels = this.gridApi?.api.getFilterModel() ?? {}
    const activeFilters = Object.keys(filterModels)
    this.clearFiltersEnabled = activeFilters.length > 0
    this.filterCount = countFilters(this.gridApi?.api) ?? 0
  }

  updateGridConfig() {
    const panelSettingsChangedEvent = new CustomEvent('panelSettingsChanged', {
      detail: {
        api: this.gridApi,
        panelSettings: this.getCurrentPanelSettingsObject(),
      } as WorkspaceUpdateEventObject,
    })
    this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
  }

  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['clearFiltersOnAccountLoad'] =
      this.clearFiltersOnAccountLoadToggle.value
    return panelSettings
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('clearFiltersOnAccountLoad' in panelSettings) {
        this.clearFiltersOnAccountLoadToggle.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )
      }
    }
  }
}
